/**
 * @file    useTableResponseSync
 * @scope   SHARED
 * @summary Turns the initial (prop) and fetched responses into table state and publishes the
 *          dropdown values they carry to the widget context.
 * @consumes WidgetContext, helpers/setNumericData
 * @produces { chartData }
 * @sideEffects Mutates response rows (setNumericData), writes to the widget context, resets the
 *              first-render guard whenever the initial response changes.
 */
import { MutableRefObject, useEffect, useState } from 'react';
import { useWidgetContext } from 'contexts/WidgetProvider';
import { setNumericData } from 'helpers/setNumericData';

interface UseTableResponseSyncArgs {
  defaultResponse: any;
  response: any;
  decimalPlaces: any;
  displayTimePeriodDelta: boolean;
  firstRender: MutableRefObject<boolean>;
  /** Builds the component-specific table state from a response. */
  onTableData: (res: any) => void;
}

export const useTableResponseSync = ({
  defaultResponse,
  response,
  decimalPlaces,
  displayTimePeriodDelta,
  firstRender,
  onTableData,
}: UseTableResponseSyncArgs) => {
  const { chartView, setState } = useWidgetContext();
  const [chartData, setChartData] = useState<any>({});

  const publishOptionsL2 = (res: any) => {
    if (res?.data?.controlData?.optionsL2) {
      setState({ optionsL2: res?.data?.controlData?.optionsL2 });
    }
  };

  useEffect(() => {
    firstRender.current = true;
    if (defaultResponse?.data?.data) {
      setState({
        selectedValueL1: defaultResponse?.data?.controlData?.defaultValueL1,
        selectedValueL2: defaultResponse?.data?.controlData?.defaultValueL2,
        defaultValueL2: defaultResponse?.data?.controlData?.defaultValueL2,
      });
      setNumericData(defaultResponse?.data?.data ? defaultResponse?.data?.data : []);
      onTableData(defaultResponse);
      publishOptionsL2(defaultResponse);
    }
  }, [defaultResponse, displayTimePeriodDelta, decimalPlaces]);

  useEffect(() => {
    if (response?.data?.data) {
      onTableData(response);
      setNumericData(response?.data?.data ? response?.data?.data : []);
      if (chartView) {
        setChartData(response?.data);
      }
      publishOptionsL2(response);
    }
  }, [response, decimalPlaces]);

  return { chartData };
};
